import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaHome,
  FaUsers,
  FaTags,
  FaBox,
  FaShoppingCart,
  FaTruck,
  FaLayerGroup,
  FaBoxOpen,
  FaBoxes,
  FaUser,
  FaFileAlt,
  FaChevronDown,
  FaChevronRight,
  FaWarehouse,
  FaClipboardList,
  FaListAlt,
  FaExclamationTriangle,
  FaImage,
  FaCreditCard,
  FaChartLine,
  FaCog,
  FaUserFriends,
  FaUserShield,
  FaUserCircle,
  FaTimesCircle,
  FaBook,
  FaUniversity,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaUndo,
  FaHistory,
} from "react-icons/fa";
import {
  PERMISSIONS,
  ROLES,
  hasPermission,
  isSuperAdmin,
  isVendor,
  isAdmin,
  getUserRoleTitle,
} from "../../utils/permissionManager";
import "./sidebar.css";


const Sidebar = ({ isOpen, onToggle }) => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  // Track if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // --- New Logic Check ---
  // Check if ANY submenu is currently expanded (used to deactivate regular links)
  const isAnyMenuExpanded = Object.values(expandedMenus).some(
    (val) => val === true
  );
  // Check if the current path is inside the reports submenu
  const isReportsPath = location.pathname.startsWith("/reports/");
  // ---

  // Auto-expand Reports menu on Reports routes
  useEffect(() => {
    if (isReportsPath) {
      setExpandedMenus((prev) => ({
        ...prev,
        reports: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Function to close all expanded menus (used when clicking a regular link)
  const collapseAllMenus = () => {
    setExpandedMenus({});
  };

  const menuItems = [
    {
      path: "/admin/vendors",
      label: "Vendors",
      icon: <FaUsers />,
      show: user && hasPermission(user, PERMISSIONS.VENDORS_READ),
    },
    {
      path: "/admin/customers",
      label: "Customers",
      icon: <FaUserFriends />,
      show: user && hasPermission(user, PERMISSIONS.CUSTOMERS_READ),
    },
    {
      key: "product_listing",
      label: "Product Listing",
      icon: <FaBox />,
      show: user && hasPermission(user, PERMISSIONS.CATEGORIES_MANAGE),
      submenu: [
        {
          path: "/category",
          label: "Category",
          icon: <FaTags />,
          show: user && hasPermission(user, PERMISSIONS.CATEGORIES_MANAGE),
        },
        {
          path: "/sub-category",
          label: "Sub Category",
          icon: <FaLayerGroup />,
          show: user && hasPermission(user, PERMISSIONS.CATEGORIES_MANAGE),
        },
        {
          path: "/product",
          label: "Product List",
          icon: <FaBox />,
          show: user && hasPermission(user, PERMISSIONS.PRODUCTS_READ),
        },
      ],
    },
    {
      key: "school_masters",
      label: "School Master",
      icon: <FaBook />,
      show: user && hasPermission(user, PERMISSIONS.MASTERS_SCHOOL),
      submenu: [
        {
          path: "/boards",
          label: "Boards",
        },
        {
          path: "/classes",
          label: "Classes",
        },
        {
          path: "/subjects",
          label: "Subjects",
        },
      ],
    },
    {
      key: "college_masters",
      label: "College Master",
      icon: <FaUniversity />,
      show: user && hasPermission(user, PERMISSIONS.MASTERS_COLLEGE),
      submenu: [
        {
          path: "/universities",
          label: "Universities",
        },
        {
          path: "/courses",
          label: "Courses",
        },
        {
          path: "/semesters",
          label: "Semesters",
        },
        {
          path: "/college-subjects",
          label: "Subjects",
        },
      ],
    },
    {
      key: "competitive_masters",
      label: "Competitive Master",
      icon: <FaGraduationCap />,
      show: user && hasPermission(user, PERMISSIONS.MASTERS_COMPETITIVE),
      submenu: [
        {
          path: "/exam-categories",
          label: "Exam Categories",
        },
        {
          path: "/exams",
          label: "Exams",
        },
        {
          path: "/competitive-subjects",
          label: "Subjects",
        },
      ],
    },
    {
      path: "/product",
      label: "Products",
      icon: <FaBox />,
      show:
        user &&
        hasPermission(user, PERMISSIONS.PRODUCTS_READ) &&
        !hasPermission(user, PERMISSIONS.CATEGORIES_MANAGE),
    },

    // {
    //   key: "inventory_management",
    //   label: "Inventory",
    //   icon: <FaWarehouse />,
    //   show:
    //     user &&
    //     hasPermission(user, [
    //       PERMISSIONS.INVENTORY_READ,
    //       PERMISSIONS.INVENTORY_ADJUST,
    //       PERMISSIONS.PURCHASES_MANAGE,
    //     ]),
    //   submenu: [
    //     {
    //       path: "/inventory",
    //       label: "Inventory Overview",
    //       icon: <FaWarehouse />,
    //       show: user && hasPermission(user, PERMISSIONS.INVENTORY_READ),
    //     },
    //     {
    //       path: "/purchase-orders",
    //       label: "Purchase Orders",
    //       icon: <FaShoppingCart />,
    //       show: user && hasPermission(user, PERMISSIONS.PURCHASES_MANAGE),
    //     },
    //     {
    //       path: "/stock-adjustments",
    //       label: "Stock Adjustments",
    //       icon: <FaBoxes />,
    //       show: user && hasPermission(user, PERMISSIONS.INVENTORY_ADJUST),
    //     },
    //   ],
    // },

    {
      path: "/banners",
      label: "Banners",
      icon: <FaImage />,
      show: user && hasPermission(user, PERMISSIONS.BANNERS_MANAGE),
    },
    {
      key: "order_management",
      label: "Order Management",
      icon: <FaBoxOpen />,
      show:
        user &&
        hasPermission(user, [
          PERMISSIONS.ORDERS_READ,
          PERMISSIONS.PAYMENTS_READ,
          PERMISSIONS.RETURNS_READ,
          PERMISSIONS.RETURNS_MANAGE,
          PERMISSIONS.REFUNDS_READ,
        ]),
      submenu: [
        {
          path: "/admin/orders",
          label: "Orders",
          icon: <FaClipboardList />,
          show: user && hasPermission(user, PERMISSIONS.ORDERS_READ),
        },
        {
          path: "/admin/payments",
          label: "Payment History",
          icon: <FaCreditCard />,
          show: user && hasPermission(user, PERMISSIONS.PAYMENTS_READ),
        },
        {
          path: "/admin/returns",
          label: "Returns & Exchanges",
          icon: <FaUndo />,
          show:
            user &&
            hasPermission(user, [
              PERMISSIONS.RETURNS_READ,
              PERMISSIONS.RETURNS_MANAGE,
            ]),
        },
      ],
    },
    {
      key: "reports",
      label: "Reports",
      icon: <FaFileAlt />,
      show:
        user &&
        (user.user_type === "delivery" ||
          hasPermission(user, [
            PERMISSIONS.REPORTS_INVENTORY,
            PERMISSIONS.REPORTS_ORDERS,
            PERMISSIONS.REPORTS_PURCHASE,
            PERMISSIONS.REPORTS_REVENUE,
            PERMISSIONS.REPORTS_LOW_STOCK,
            PERMISSIONS.REPORTS_CANCELLED_ORDERS,
          ])),
      submenu: [
        {
          path: "/reports/inventory",
          label: "Inventory Report",
          icon: <FaWarehouse />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_INVENTORY),
        },
        {
          path: "/reports/purchase",
          label: "Purchase Report",
          icon: <FaClipboardList />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_PURCHASE),
        },
        {
          path: "/reports/orders",
          label: "Orders Report",
          icon: <FaListAlt />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_ORDERS),
        },
        {
          path: "/reports/cancelled-orders",
          label: "Cancelled Orders",
          icon: <FaTimesCircle />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_CANCELLED_ORDERS),
        },
        {
          path: "/reports/low-stock",
          label: "Low Stock Products",
          icon: <FaExclamationTriangle />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_LOW_STOCK),
        },
        {
          path: "/reports/revenue",
          label: "Revenue Report",
          icon: <FaChartLine />,
          show: user && hasPermission(user, PERMISSIONS.REPORTS_REVENUE),
        },
      ],
    },
    {
      path: "/admin/roles",
      label: "Roles & Permissions",
      icon: <FaUserShield />,
      show:
        user &&
        hasPermission(user, [
          PERMISSIONS.ROLES_MANAGE,
          PERMISSIONS.USERS_MANAGE_ROLES,
        ]),
    },
    {
      path: "/admin/activity-logs",
      label: "Activity Logs",
      icon: <FaHistory />,
      show:
        user &&
        hasPermission(user, [
          PERMISSIONS.ACTIVITY_LOGS_READ,
          PERMISSIONS.ROLES_MANAGE,
        ]),
    },
    {
      path: "/admin/users",
      label: "Users",
      icon: <FaUsers />,
      show:
        user &&
        hasPermission(user, [
          PERMISSIONS.USERS_READ,
          PERMISSIONS.USERS_MANAGE,
        ]),
    },
    {
      path: "/profile",
      label: "Profile",
      icon: <FaUserCircle />,
      show: !!user,
    },
  ];

  // Auto-expand and persist active parent menus based on current route
  useEffect(() => {
    const newExpanded = { ...expandedMenus };
    let changed = false;

    menuItems.forEach((item) => {
      if (item.submenu) {
        const isChildActive = item.submenu.some(subItem => {
          if (subItem.submenu) {
            return subItem.submenu.some(innerItem => location.pathname === innerItem.path);
          }
          return location.pathname === subItem.path;
        });

        if (isChildActive && !expandedMenus[item.key]) {
          newExpanded[item.key] = true;
          changed = true;
        }
      }
    });

    if (isReportsPath && !expandedMenus.reports) {
      newExpanded.reports = true;
      changed = true;
    }

    if (changed) {
      setExpandedMenus(newExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Overlay - Only show on mobile screens when sidebar is open */}
      {isOpen && isMobile && (
        <div className="sidebar-overlay" onClick={onToggle}></div>
      )}

      <div className={`sidebar ${isOpen ? "open" : ""}`} style={{ backgroundImage: "" }}>
        {/* Top Section with Yellow Background */}
        <div className="top-sidebar">
          <div className="sidebar-logo-wrapper">
            <img
              src={process.env.PUBLIC_URL + "/book-edu-logo.png"}
              alt="BookEdu"
              className="sidebar-logo-main"
            />
            <h2 className="sidebar-logo-text">BookEdu</h2>
          </div>
        </div>


        {/* Navigation Menu */}
        <div className="sidebar-content">
          <nav className="sidebar-nav">
            <ul className="nav-list">
              {menuItems.map((item) => {
                if (!item.show) return null;

                if (item.submenu) {
                  const isExpanded = expandedMenus[item.key] || false;
                  // Check if any child is active
                  const isChildActive = item.submenu.some(subItem => {
                    if (subItem.submenu) {
                      return subItem.submenu.some(innerItem => location.pathname === innerItem.path);
                    }
                    return location.pathname === subItem.path;
                  });

                  return (
                    <li
                      key={item.key}
                      className={`nav-item ${isChildActive ? "child-active" : ""
                        } ${isExpanded ? "expanded" : ""}`}
                    >
                      <div
                        className="nav-link nav-link-with-submenu"
                        onClick={() => toggleMenu(item.key)}
                      >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-chevron">
                          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                      </div>

                      {isExpanded && (
                        <ul className="submenu-list">
                          {item.submenu.filter(sub => sub.show !== false).map((subItem) => {
                            if (subItem.submenu) {
                              const isSubExpanded = expandedMenus[`${item.key}_${subItem.key}`] || false;
                              const isInnerChildActive = subItem.submenu.some(innerItem => location.pathname === innerItem.path);

                              return (
                                <li key={subItem.key} className={`submenu-group ${isInnerChildActive ? "active" : ""} ${isSubExpanded ? "expanded" : ""}`}>
                                  <div className="submenu-group-header" onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMenu(`${item.key}_${subItem.key}`);
                                  }}>
                                    {subItem.icon && <span className="submenu-icon">{subItem.icon}</span>}
                                    <span className="submenu-label">{subItem.label}</span>
                                    <span className="nav-chevron ms-auto">
                                      {isSubExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                    </span>
                                  </div>
                                  {isSubExpanded && (
                                    <ul className="inner-submenu-list">
                                      {subItem.submenu.map((innerItem) => {
                                        const isInnerActive = location.pathname === innerItem.path;
                                        return (
                                          <li key={innerItem.path} className={`inner-submenu-item ${isInnerActive ? "active" : ""}`}>
                                            <Link
                                              to={innerItem.path}
                                              className="inner-submenu-link"
                                              onClick={() => {
                                                if (window.innerWidth <= 768 && isOpen) onToggle();
                                              }}
                                            >
                                              <span className="inner-submenu-label">{innerItem.label}</span>
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </li>
                              );
                            }

                            const isSubActive = location.pathname === subItem.path;

                            return (
                              <li
                                key={subItem.path}
                                className={`submenu-item ${isSubActive ? "active" : ""
                                  }`}
                              >
                                <Link
                                  to={subItem.path}
                                  className="submenu-link"
                                  onClick={() => {
                                    if (window.innerWidth <= 768 && isOpen)
                                      onToggle();
                                  }}
                                >
                                  {subItem.icon && (
                                    <span className="submenu-icon">
                                      {subItem.icon}
                                    </span>
                                  )}
                                  <span className="submenu-label">
                                    {subItem.label}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                // Handle regular menu items
                const isActive =
                  location.pathname === item.path ||
                  (item.path !== "/" &&
                    location.pathname.startsWith(item.path));

                // CRITICAL FIX: The link is ONLY active if (1) its path matches
                // AND (2) NO submenu is currently expanded (or a reports URL is active).
                const finalIsActive =
                  isActive && !isAnyMenuExpanded && !isReportsPath;

                return (
                  <li
                    key={item.path || item.key}
                    className={`nav-item ${finalIsActive ? "active" : ""}`} // Use finalIsActive
                  >
                    <Link
                      to={item.path}
                      className="nav-link"
                      onClick={collapseAllMenus} // Collapse reports menu on regular link click
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
